const API_BASE = process.env.REACT_APP_API_URL || "/api/v1";

async function parseJsonBody(response) {
  const text = await response.text();
  const trimmed = text.trim();
  if (!trimmed) return {};
  try {
    return JSON.parse(trimmed);
  } catch {
    const looksLikeHtml = /^<!DOCTYPE/i.test(trimmed) || /^<html/i.test(trimmed);
    const err = new Error(
      looksLikeHtml
        ? "Server returned a web page instead of JSON. Check REACT_APP_API_URL and that your reverse proxy forwards /api to the API."
        : "Server returned invalid JSON"
    );
    err.status = response.status;
    err.bodySnippet = trimmed.slice(0, 120);
    throw err;
  }
}

/**
 * Lightweight fetch wrapper for the MCVerse API.
 * Handles JSON, auth headers, and token refresh.
 */
class ApiClient {
  constructor() {
    this.accessToken = localStorage.getItem("accessToken");
    this.refreshToken = localStorage.getItem("refreshToken");
    this.onAuthChange = null; // set by AuthContext — logout (player = null)
    this.onAuthRestore = null; // set by AuthContext — hydrate session from tokens
    this.refreshPromise = null;
  }

  syncTokensFromStorage() {
    if (typeof localStorage === "undefined") return;
    this.accessToken = localStorage.getItem("accessToken");
    this.refreshToken = localStorage.getItem("refreshToken");
  }

  setTokens(accessToken, refreshToken) {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    if (accessToken) {
      localStorage.setItem("accessToken", accessToken);
    } else {
      localStorage.removeItem("accessToken");
    }
    if (refreshToken) {
      localStorage.setItem("refreshToken", refreshToken);
    } else {
      localStorage.removeItem("refreshToken");
    }
  }

  clearTokens() {
    this.setTokens(null, null);
  }

  async request(path, options = {}) {
    const url = `${API_BASE}${path}`;
    const headers = {
      "Content-Type": "application/json",
      ...options.headers,
    };

    if (this.accessToken) {
      headers["Authorization"] = `Bearer ${this.accessToken}`;
    }

    let response = await fetch(url, { ...options, headers });

    if (response.status === 401) {
      // Another tab may have refreshed already — pick up its tokens before rotating again.
      this.syncTokensFromStorage();
      if (this.accessToken) {
        headers["Authorization"] = `Bearer ${this.accessToken}`;
        response = await fetch(url, { ...options, headers });
      }

      if (response.status === 401 && this.refreshToken) {
        const refreshed = await this.tryRefresh();
        if (refreshed && this.accessToken) {
          headers["Authorization"] = `Bearer ${this.accessToken}`;
          response = await fetch(url, { ...options, headers });
        }
      }
    }

    const data = await parseJsonBody(response);

    if (!response.ok) {
      const error = new Error(data.error || "Request failed");
      error.status = response.status;
      error.data = data;
      throw error;
    }

    return data;
  }

  async tryRefresh() {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.performRefresh().finally(() => {
      this.refreshPromise = null;
    });

    return this.refreshPromise;
  }

  async performRefresh() {
    const refreshTokenUsed = this.refreshToken;
    if (!refreshTokenUsed) {
      return false;
    }

    try {
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: refreshTokenUsed }),
      });

      if (!response.ok) {
        if (this.adoptTokensRotatedElsewhere(refreshTokenUsed)) {
          return true;
        }
        this.clearTokens();
        if (this.onAuthChange) this.onAuthChange(null);
        return false;
      }

      const data = await parseJsonBody(response);
      this.setTokens(data.accessToken, data.refreshToken);
      return true;
    } catch {
      if (this.adoptTokensRotatedElsewhere(refreshTokenUsed)) {
        return true;
      }
      this.clearTokens();
      if (this.onAuthChange) this.onAuthChange(null);
      return false;
    }
  }

  /**
   * If another tab refreshed first, localStorage already has the new token pair.
   * Adopt them instead of clearing the session for a stale single-use refresh token.
   */
  adoptTokensRotatedElsewhere(refreshTokenUsed) {
    this.syncTokensFromStorage();
    return Boolean(
      this.accessToken &&
        this.refreshToken &&
        this.refreshToken !== refreshTokenUsed
    );
  }

  // Convenience methods
  get(path) {
    return this.request(path);
  }

  post(path, body) {
    return this.request(path, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  put(path, body) {
    return this.request(path, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  }

  patch(path, body) {
    return this.request(path, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  }

  delete(path) {
    return this.request(path, { method: "DELETE" });
  }
}

const apiClient = new ApiClient();

if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key !== "accessToken" && event.key !== "refreshToken") {
      return;
    }

    const hadTokens = Boolean(apiClient.accessToken || apiClient.refreshToken);
    apiClient.syncTokensFromStorage();
    const hasTokens = Boolean(apiClient.accessToken || apiClient.refreshToken);

    if (hadTokens && !hasTokens) {
      if (apiClient.onAuthChange) apiClient.onAuthChange(null);
      return;
    }

    if (!hasTokens || !apiClient.onAuthRestore) {
      return;
    }

    const tokensAdded = !hadTokens;
    const tokensRotated = Boolean(event.newValue && event.newValue !== event.oldValue);
    if (tokensAdded || tokensRotated) {
      apiClient.onAuthRestore();
    }
  });
}

export default apiClient;
